import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { toast } from 'sonner'

export type PlanImportParseSuccess<T> = {
  ok: true
  summary: string
  value: T
}

export type PlanImportParseFailure = {
  ok: false
}

export type PlanImportParseResult<T> =
  | PlanImportParseSuccess<T>
  | PlanImportParseFailure

type Props<T> = {
  open: boolean
  onOpenChange: (open: boolean) => void
  hasExistingContent: boolean
  description: string
  invalidMessage: string
  overwriteConfirmMessage: string
  parse: (input: string) => PlanImportParseResult<T>
  onImport: (value: T) => void | Promise<void>
}

const PlanImportStringDialog = <T,>({
  open,
  onOpenChange,
  hasExistingContent,
  description,
  invalidMessage,
  overwriteConfirmMessage,
  parse,
  onImport,
}: Props<T>) => {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pendingImportRef = useRef<T | null>(null)
  const [value, setValue] = useState('')
  const [isOverwriteConfirmOpen, setIsOverwriteConfirmOpen] = useState(false)
  const [isImporting, setIsImporting] = useState(false)

  useEffect(() => {
    if (!open) {
      setValue('')
      setIsImporting(false)
      return
    }
    const frame = requestAnimationFrame(() => {
      textareaRef.current?.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [open])

  const parseResult = useMemo(() => {
    if (value.trim() === '') return null
    return parse(value)
  }, [parse, value])

  const isValid = parseResult?.ok === true

  const applyImport = async (imported: T) => {
    if (isImporting) return
    setIsImporting(true)
    try {
      await onImport(imported)
      toast.success('Plan imported')
      pendingImportRef.current = null
      setIsOverwriteConfirmOpen(false)
      onOpenChange(false)
    } finally {
      setIsImporting(false)
    }
  }

  const handleImportClick = () => {
    if (!parseResult?.ok) return
    if (hasExistingContent) {
      pendingImportRef.current = parseResult.value
      setIsOverwriteConfirmOpen(true)
      onOpenChange(false)
      return
    }
    void applyImport(parseResult.value)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className='max-w-lg'
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.team.plans.transfer.import-title')}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              rows={8}
              className='break-all font-mono text-xs'
              placeholder={description}
            />
            {parseResult?.ok ? (
              <p className='text-sm text-muted-foreground'>
                {parseResult.summary}
              </p>
            ) : null}
            {parseResult && !parseResult.ok ? (
              <p className='text-sm text-destructive-foreground'>
                {invalidMessage}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('dashboard.team.plans.transfer.close')}
            </Button>
            <Button
              type='button'
              disabled={!isValid || isImporting}
              loading={isImporting}
              onClick={handleImportClick}
            >
              {t('dashboard.team.plans.transfer.import-action')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={isOverwriteConfirmOpen}
        description={overwriteConfirmMessage}
        continueText={t('dashboard.team.plans.transfer.import-action')}
        onCancel={() => {
          pendingImportRef.current = null
          setIsOverwriteConfirmOpen(false)
        }}
        onContinue={() => {
          if (pendingImportRef.current === null) return
          return applyImport(pendingImportRef.current)
        }}
        variant='destructive'
      />
    </>
  )
}

export default PlanImportStringDialog
